import { NetInfoStateType } from '@react-native-community/netinfo';
import { AxiosError } from 'axios';

import { RBDeviceType } from 'store/railsBuilder/reducer.types.ts';

import LANGUAGES_NAMES from 'utils/Language/languageNames';

import { RequestStatus } from '../utils';

export type Profile = {
    name: string;
    _id: string;
    pinPromptRequired: boolean;
    iconName: string;
    childProfile: boolean;
    accountId: string;
    default: boolean;
    maxAgeRating: number;
    created: string;
    modified: string;
    ratingsThresholds: {
        ratingsBody: string;
        ratingsThreshold: ParentalControlRating;
    }[];
};

export enum CapabilityTypes {
    CU = 'CU',
    SO = 'SO',
    Timeshift = 'Timeshift',
    Purchase = 'Purchase',
    NPVR = 'NPVR',
    MUST_CARRY = 'MUST_CARRY',
    NON_INTERACTIVE = 'NON_INTERACTIVE',
}

export type Capability = {
    _id: string;
    productId: string;
    accountId: string;
    status: string;
    validityType: string;
    productType: string;
    validFrom: string;
    expiryDate: string;
    created: string;
    modified: string;
    isArchivable: boolean;
    capabilityType: CapabilityTypes;
    isFreemium: boolean;
};

export type Entitlement = {
    _id: string;
    productId: string;
    accountId: string;
    status: string;
    validityType: string;
    productType: string;
    validFrom: string;
    expiryDate: string;
    created: string;
    modified: string;
    isArchivable: boolean;
    capabilityType: CapabilityTypes;
    isFreemium: boolean;
};

export type CapabilitiesResponse = {
    totalRecords: number;
    resourceSet: Capability[];
};

export type EntitlementsResponse = {
    totalRecords: number;
    resourceSet: Entitlement[];
};

export type ProfileParentalRating = {
    rating: ParentalControlRating;
    key: string;
};

export type UserState = {
    language: keyof typeof LANGUAGES_NAMES;
    loginRequest: {
        status: RequestStatus;
        error?: AuthError;
    };
    loginSilentlyRequest: {
        status: RequestStatus;
        error: null | string | AxiosError;
    };
    logoutRequest: {
        status: RequestStatus;
        error: null | AxiosError;
    };
    selectedProfile: string | undefined;
    isGuest: boolean;
    auth?: AuthData;
    clientId?: string;
    entitlementsRequest: {
        status: RequestStatus;
        error: null | AxiosError;
        data: EntitlementsResponse['resourceSet'];
    };
    updateRatingsThresholdsRequest: {
        status: RequestStatus;
        error: null | AxiosError;
    };
    modalVisible: boolean;
    pinsStatusRequest: {
        status: RequestStatus;
        error: null | AxiosError;
        data: PinStatus[];
    };
    refreshTokenError: null | AxiosError;
    favouriteChannelsListRequest: {
        status: RequestStatus;
        error: null | AxiosError;
        data: FavouritesChannelsListsResponse['favouritesLists'];
    };
    updateFavouriteChannelsListRequest: {
        status: RequestStatus;
        error: null | AxiosError;
    };
    profilesListRequest: {
        status: RequestStatus;
        error: null | AxiosError;
        data: ProfilesListsResponse['resourceSet'];
    };
    changeUserProfileRequest: {
        status: RequestStatus;
        error: null | AxiosError;
    };
    addProfileRequest: {
        status: RequestStatus;
        error: null | AxiosError;
    };
    profileParentalRating: ProfileParentalRating | null;
    profileAvatar: string | null;
    currentProfileAvatar: string | undefined;
    editProfileRequest: {
        status: RequestStatus;
        error: null | AxiosError;
    };
    deleteProfileRequest: {
        status: RequestStatus;
        error: null | AxiosError;
    };
    OTTLastConnectionTime: string | undefined;
    lastProvisionDate: string | undefined;
    suspension: SUSPENSION_TYPES | undefined;
    cashedPinByParentalRating:
        | {
              [key: string]: {
                  [key in Partial<ParentalControlRating>]?: number;
              };
          }
        | undefined;
    isLineupChanged: boolean;
    shouldLineupModalShow: boolean;
    connectionType: NetInfoStateType | undefined;
    sharedConsentPublicId: string | undefined;
};

export type CatchRequestError = Error | AxiosError | unknown;

export interface AuthData {
    parentalControl?: ParentalControl;
    refresh_token: string;
    access_token: string;
    client_id: string;
    fixed_refresh_expires_in: number;
    accountId: string;
    refresh_expires_in: number;
    expires_in: number;
    token_type: string;
    creation_time: number;
    currentUserProfileId: string;
}

export type AuthResponseError = {
    localeMessage: string;
    requestId: number;
    result: string;
    resultCode: string;
};

export type AuthError = {
    message: string;
    permanent: boolean;
    code?: string;
    status?: number;
};

export type ParentalControl = {
    ratingThreshold?: {
        HCRS?: ParentalControlRating;
    };
};

export enum ParentalControlRating {
    HU_GENERAL = 'hu-gy',
    HU_UNRATED = 'hu-unrated',
    HU6 = 'hu-6',
    HU12 = 'hu-12',
    HU16 = 'hu-16',
    HU18 = 'hu-18',
    HU18_ADULT_CENSORED = 'hu-18-adult',
    HU18_ADULT_UNCENSORED = 'hu-18-adult-uc',
    UNKNOWN = 'unknown',
}

export enum PIN_STATUS {
    SET = 'SET',
    UNSET = 'UNSET',
}

export enum PIN_TYPES {
    ParentalPin = 'ParentalPin',
    AccessPin = 'AccessPin',
    PurchasePin = 'PurchasePin',
}

export enum PIN_EXTENDED_STATUS {
    OPTIONAL = 'OPTIONAL',
    MANDATORY = 'MANDATORY',
}

export type PinStatus = {
    pinType: PIN_TYPES;
    status: PIN_STATUS;
    extendedStatus: PIN_EXTENDED_STATUS;
    lockTimeRemaining: number;
};

export type PinsStatusResponse = {
    totalRecords: number;
    resourceSet: PinStatus[];
};

export type OriginalFavouriteChannelsListMetadata = {
    metadata: Record<string, string>;
};

export type FavouriteChannelsListMetadataItem = {
    channelId: string;
    isHidden: boolean;
    sortIndex: number;
};

export type FavouritesChannelsList = {
    id: string;
    account: AuthData['accountId'];
    userId: string;
    name: string;
    creationDate: string;
    modifiedDate: string;
    tags: string[];
    tenantId: string;
};

export type FavouritesChannelsListsResponse = {
    favouritesLists: (FavouritesChannelsList &
        OriginalFavouriteChannelsListMetadata)[];
    totalRecords: number;
};

export type ProfilesListsResponse = {
    resourceSet: Profile[];
    totalRecords: number;
};

export type AddProfileResponse = {
    _id: string;
};

export type RequestErrorData = {
    code: number;
    errorCode: number;
    message: string;
};

export type SignInRequestPayload = {
    parameters:
        | [
              {
                  name: 'username';
                  value: string;
              },
              {
                  name: 'password';
                  value: string;
              },
          ]
        | [
              {
                  name: 'sessionToken';
                  value: string;
              },
          ];
    clientId?: string;
    deviceInformation?: {
        playoutDeviceClass: 'browser' | 'mobileDevice';
        device: {
            screen: {
                height: number;
                width: number;
            };
            hardware: {
                model: string;
                type: 'Browser' | 'Tablet' | 'Mobile';
                manufacturer: string;
            };
            OS: {
                type: string;
                version: string;
            };
        };
    };
};

export enum SUSPENSION_TYPES {
    VOLUNTARY = 'VOLUNTARY',
    COLLECTION = 'COLLECTION',
}
